import {useEffect, useRef, useState} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {css, cx} from "@panda/css";
import {AnimatePresence, motion} from "framer-motion";

import {Button} from "@pg-design/button-module";
import {calculateRemSize} from "@pg-design/helpers-css-strings";
import {MAX_VALUE_INPUT_LIMITER} from "@pg-design/inputs-module";
import {MaxValueFieldControlled, SelectRangeFieldControlled, SwitcherControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {Currency} from "@pg-rp/common-types";

import {PreferencesRegionField} from "../../application/components/preferences_steps/components/PreferencesRegionField";
import {PreferencesRegionMap} from "../../application/components/preferences_steps/components/PreferencesRegionMap";
import {FOR_YOU_FORM_LABELS} from "../../application/constants/form_labels";
import {PREFERENCES_FORM_STEP} from "../../application/constants/preferences_form";
import {IPreferencesDefaultValues} from "../../application/types/IPreferencesDefaultValues";
import {getCurrency} from "../../utils/get_currency";
import {PreferencesFormHeader} from "./prefences_form/PreferencesFormHeader";

interface IProps {
    handleSubmit: () => void;
    header?: React.ReactNode;
    topFormContent?: React.ReactNode;
    currency: Currency;
    maxBudget: number;
    formWrapperClassName?: string;
    onFieldInteraction?: (section: string, value: string) => void;
}

export const PreferencesForm = (props: IProps) => {
    const {handleSubmit, currency, header, topFormContent, formWrapperClassName, onFieldInteraction} = props;
    const [isLocationFocused, setIsLocationFocused] = useState(false);
    const {control} = useFormContext<IPreferencesDefaultValues>();

    const budgetValue = useWatch({control, name: "budget"});
    const roomsValue = useWatch({control, name: "rooms"});
    const creditPlanValue = useWatch({control, name: "creditPlan"});

    const onInputClick = (defaultAction: () => void) => {
        defaultAction();
        setIsLocationFocused(true);
    };

    const onActionButtonClick = () => {
        setIsLocationFocused(false);
    };

    const formatBudget = (value: number | undefined): string => {
        if (!value || value >= props.maxBudget) {
            return "bez limitu";
        }
        return `${value.toLocaleString("pl-PL")} zł`;
    };

    const formatRooms = (rooms: {lower: number | string; upper: number | string}): string => {
        const lower = typeof rooms.lower === "number" ? rooms.lower : "";
        const upper = typeof rooms.upper === "number" ? rooms.upper : "";

        if (lower && upper) {
            if (lower === upper) {
                return `${lower}`;
            }
            return `${lower}-${upper}`;
        }
        if (lower) {
            return `${lower}`;
        }
        if (upper) {
            return `${upper}`;
        }
        return "";
    };

    const handleRegionChange = (regionName: string) => {
        if (onFieldInteraction) {
            onFieldInteraction(FOR_YOU_FORM_LABELS.regionSearch, regionName);
        }
    };

    const budgetTracked = useRef(false);
    const roomsTracked = useRef(false);
    const creditPlanTracked = useRef(false);

    useEffect(() => {
        if (budgetTracked.current && onFieldInteraction && budgetValue !== undefined && budgetValue !== null && budgetValue < MAX_VALUE_INPUT_LIMITER) {
            onFieldInteraction(FOR_YOU_FORM_LABELS.budget, formatBudget(budgetValue));
        }
        budgetTracked.current = true;
    }, [budgetValue]);

    useEffect(() => {
        if (roomsTracked.current && onFieldInteraction && roomsValue) {
            const formattedValue = formatRooms(roomsValue);
            if (formattedValue) {
                onFieldInteraction(FOR_YOU_FORM_LABELS.rooms, formattedValue);
            }
        }
        roomsTracked.current = true;
    }, [roomsValue]);

    useEffect(() => {
        if (creditPlanTracked.current && onFieldInteraction && creditPlanValue !== undefined) {
            onFieldInteraction(FOR_YOU_FORM_LABELS.creditPlan, creditPlanValue ? "tak" : "nie");
        }
        creditPlanTracked.current = true;
    }, [creditPlanValue]);

    return (
        <div>
            {header ? (
                <motion.div
                    animate={{
                        opacity: isLocationFocused ? 0 : 1,
                        height: isLocationFocused ? 0 : "auto"
                    }}
                    transition={transition}
                >
                    {header}
                </motion.div>
            ) : null}
            <motion.div
                className={locationHeading}
                animate={{
                    opacity: isLocationFocused ? 1 : 0,
                    height: isLocationFocused ? "auto" : 0
                }}
                transition={transition}
            >
                <Text variant="headline_6" as="p">
                    Wybierz lokalizację
                </Text>
            </motion.div>
            <form onSubmit={handleSubmit} className={formWrapperClassName}>
                <div className={cx(formStyle, !isLocationFocused && formLocationStyle)}>
                    {topFormContent ? (
                        <motion.div
                            animate={{
                                opacity: isLocationFocused ? 0 : 1,
                                height: isLocationFocused ? 0 : "auto"
                            }}
                            transition={transition}
                        >
                            {topFormContent}
                        </motion.div>
                    ) : null}
                    <motion.div
                        animate={{
                            marginTop: isLocationFocused ? 0 : calculateRemSize(4)
                        }}
                        transition={transition}
                    >
                        <PreferencesRegionField
                            name="regionSelector"
                            label={isLocationFocused ? undefined : FOR_YOU_FORM_LABELS.regionSearch}
                            onInputClick={onInputClick}
                            onRegionSelect={handleRegionChange}
                        />
                    </motion.div>
                    <motion.div
                        animate={{
                            opacity: isLocationFocused ? 0 : 1,
                            height: isLocationFocused ? 0 : "auto"
                        }}
                        transition={transition}
                    >
                        <div className={preferencesWrapper}>
                            <MaxValueFieldControlled
                                name="budget"
                                min={0}
                                max={props.maxBudget}
                                step={PREFERENCES_FORM_STEP}
                                rightElement={getCurrency({currency})}
                                labelContent={FOR_YOU_FORM_LABELS.budget}
                                className={sliderStyle}
                                inputClassName={inputStyle}
                                maxDisplayLabel="bez limitu"
                                centerValue={500_000}
                            />
                            <div className={switcherMobile}>
                                <SwitcherControlled labelContent={FOR_YOU_FORM_LABELS.creditPlan} name="creditPlan" wrapperClassName={switchStyle} />
                            </div>
                            <div className={roomsSelectWrapper}>
                                <Text variant="body_copy_2">{FOR_YOU_FORM_LABELS.rooms}</Text>
                                <SelectRangeFieldControlled
                                    name="rooms"
                                    options={[
                                        {id: "1", label: "1", value: 1},
                                        {id: "2", label: "2", value: 2},
                                        {id: "3", label: "3", value: 3},
                                        {id: "4", label: "4", value: 4},
                                        {id: "5", label: "5+", value: 5}
                                    ]}
                                    className={roomsSelectStyle}
                                    itemClassName={rangeSelectItem}
                                />
                            </div>
                        </div>
                        <div className={switcherDesktop}>
                            <SwitcherControlled labelContent={FOR_YOU_FORM_LABELS.creditPlan} name="creditPlan" wrapperClassName={switchStyle} />
                        </div>
                        <Button variant="filled_primary" size="medium" type="submit" className={btnStyle}>
                            Zapisz swoje preferencje
                        </Button>
                    </motion.div>
                    <AnimatePresence>
                        {isLocationFocused && (
                            <motion.div
                                initial={{opacity: 0, height: 0}}
                                animate={{opacity: 1, height: "auto"}}
                                exit={{opacity: 0, height: 0}}
                                transition={transition}
                            >
                                <div className={mapHolder}>
                                    <PreferencesRegionMap onActionButtonClick={onActionButtonClick} />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </form>
        </div>
    );
};

PreferencesForm.Header = PreferencesFormHeader;

const transition = {
    type: "spring",
    stiffness: 300,
    damping: 30
};

const formStyle = css({
    px: 1.5,
    py: 2,
    borderRadius: 1,

    md: {
        p: 2
    }
});

const formLocationStyle = css({
    background: "var(--colors-gray-100)",

    md: {
        mt: 3
    }
});

const locationHeading = css({
    pt: 2.5,
    pl: 1.5,

    md: {
        p: 0
    }
});

const preferencesWrapper = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "column",
    gap: 3,
    mt: 2,

    md: {
        alignItems: "start",
        flexDirection: "row"
    }
});

const switcher = css.raw({
    p: 1,
    borderRadius: 1,
    background: "var(--colors-gray-200)"
});

const switcherDesktop = css({
    ...switcher,
    display: "none",
    mt: 2,

    md: {
        display: "block"
    }
});

const switcherMobile = css({
    ...switcher,
    width: "100%",

    md: {
        display: "none"
    }
});

const btnStyle = css({
    width: "100%",
    mt: 2
});

const roomsSelectWrapper = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5
});

const roomsSelectStyle = css({
    width: "300px"
});

const sliderStyle = css({
    pl: 1,

    md: {
        pl: 0
    }
});

const inputStyle = css({
    width: "75px"
});

const rangeSelectItem = css({
    height: "48px",
    borderColor: "var(--colors-gray-600) !important"
});

const switchStyle = css({
    display: "flex",
    justifyContent: "flex-end",
    flexDirection: "row-reverse",
    gap: 1
});

const mapHolder = css({
    minHeight: "460px"
});
import {useEffect, useRef, useState} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {css, cx} from "@panda/css";
import {AnimatePresence, motion} from "framer-motion";

import {Button} from "@pg-design/button-module";
import {calculateRemSize} from "@pg-design/helpers-css-strings";
import {MAX_VALUE_INPUT_LIMITER} from "@pg-design/inputs-module";
import {MaxValueFieldControlled, SelectRangeFieldControlled, SwitcherControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {Currency} from "@pg-rp/common-types";

import {PreferencesRegionField} from "../../application/components/preferences_steps/components/PreferencesRegionField";
import {PreferencesRegionMap} from "../../application/components/preferences_steps/components/PreferencesRegionMap";
import {FOR_YOU_FORM_LABELS} from "../../application/constants/form_labels";
import {PREFERENCES_FORM_STEP} from "../../application/constants/preferences_form";
import {IPreferencesDefaultValues} from "../../application/types/IPreferencesDefaultValues";
import {getCurrency} from "../../utils/get_currency";
import {PreferencesFormHeader} from "./prefences_form/PreferencesFormHeader";

interface IProps {
    handleSubmit: () => void;
    header?: React.ReactNode;
    topFormContent?: React.ReactNode;
    currency: Currency;
    maxBudget: number;
    formWrapperClassName?: string;
    onFieldInteraction?: (section: string, value: string) => void;
}

export const PreferencesForm = (props: IProps) => {
    const {handleSubmit, currency, header, topFormContent, formWrapperClassName, onFieldInteraction} = props;
    const [isLocationFocused, setIsLocationFocused] = useState(false);
    const {control} = useFormContext<IPreferencesDefaultValues>();

    const budgetValue = useWatch({control, name: "budget"});
    const roomsValue = useWatch({control, name: "rooms"});
    const creditPlanValue = useWatch({control, name: "creditPlan"});

    const onInputClick = (defaultAction: () => void) => {
        defaultAction();
        setIsLocationFocused(true);
    };

    const onActionButtonClick = () => {
        setIsLocationFocused(false);
    };

    const formatBudget = (value: number | undefined): string => {
        if (!value || value >= props.maxBudget) {
            return "bez limitu";
        }
        return `${value.toLocaleString("pl-PL")} zł`;
    };

    const formatRooms = (rooms: {lower: number | string; upper: number | string}): string => {
        const lower = typeof rooms.lower === "number" ? rooms.lower : "";
        const upper = typeof rooms.upper === "number" ? rooms.upper : "";

        if (lower && upper) {
            if (lower === upper) {
                return `${lower}`;
            }
            return `${lower}-${upper}`;
        }
        if (lower) {
            return `${lower}`;
        }
        if (upper) {
            return `${upper}`;
        }
        return "";
    };

    const handleRegionChange = (regionName: string) => {
        if (onFieldInteraction) {
            onFieldInteraction(FOR_YOU_FORM_LABELS.regionSearch, regionName);
        }
    };

    const budgetTracked = useRef(false);
    const roomsTracked = useRef(false);
    const creditPlanTracked = useRef(false);

    useEffect(() => {
        if (budgetTracked.current && onFieldInteraction && budgetValue !== undefined && budgetValue !== null && budgetValue < MAX_VALUE_INPUT_LIMITER) {
            onFieldInteraction(FOR_YOU_FORM_LABELS.budget, formatBudget(budgetValue));
        }
        budgetTracked.current = true;
    }, [budgetValue]);

    useEffect(() => {
        if (roomsTracked.current && onFieldInteraction && roomsValue) {
            const formattedValue = formatRooms(roomsValue);
            if (formattedValue) {
                onFieldInteraction(FOR_YOU_FORM_LABELS.rooms, formattedValue);
            }
        }
        roomsTracked.current = true;
    }, [roomsValue]);

    useEffect(() => {
        if (creditPlanTracked.current && onFieldInteraction && creditPlanValue !== undefined) {
            onFieldInteraction(FOR_YOU_FORM_LABELS.creditPlan, creditPlanValue ? "tak" : "nie");
        }
        creditPlanTracked.current = true;
    }, [creditPlanValue]);

    return (
        <div>
            {header ? (
                <motion.div
                    animate={{
                        opacity: isLocationFocused ? 0 : 1,
                        height: isLocationFocused ? 0 : "auto"
                    }}
                    transition={transition}
                >
                    {header}
                </motion.div>
            ) : null}
            <motion.div
                className={locationHeading}
                animate={{
                    opacity: isLocationFocused ? 1 : 0,
                    height: isLocationFocused ? "auto" : 0
                }}
                transition={transition}
            >
                <Text variant="headline_6" as="p">
                    Wybierz lokalizację
                </Text>
            </motion.div>
            <form onSubmit={handleSubmit} className={formWrapperClassName}>
                <div className={cx(formStyle, !isLocationFocused && formLocationStyle)}>
                    {topFormContent ? (
                        <motion.div
                            animate={{
                                opacity: isLocationFocused ? 0 : 1,
                                height: isLocationFocused ? 0 : "auto"
                            }}
                            transition={transition}
                        >
                            {topFormContent}
                        </motion.div>
                    ) : null}
                    <motion.div
                        animate={{
                            marginTop: isLocationFocused ? 0 : calculateRemSize(4)
                        }}
                        transition={transition}
                    >
                        <PreferencesRegionField
                            name="regionSelector"
                            label={isLocationFocused ? undefined : FOR_YOU_FORM_LABELS.regionSearch}
                            onInputClick={onInputClick}
                            onRegionSelect={handleRegionChange}
                        />
                    </motion.div>
                    <motion.div
                        animate={{
                            opacity: isLocationFocused ? 0 : 1,
                            height: isLocationFocused ? 0 : "auto"
                        }}
                        transition={transition}
                    >
                        <div className={preferencesWrapper}>
                            <MaxValueFieldControlled
                                name="budget"
                                min={0}
                                max={props.maxBudget}
                                step={PREFERENCES_FORM_STEP}
                                rightElement={getCurrency({currency})}
                                labelContent={FOR_YOU_FORM_LABELS.budget}
                                className={sliderStyle}
                                inputClassName={inputStyle}
                                maxDisplayLabel="bez limitu"
                                centerValue={500_000}
                            />
                            <div className={switcherMobile}>
                                <SwitcherControlled labelContent={FOR_YOU_FORM_LABELS.creditPlan} name="creditPlan" wrapperClassName={switchStyle} />
                            </div>
                            <div className={roomsSelectWrapper}>
                                <Text variant="body_copy_2">{FOR_YOU_FORM_LABELS.rooms}</Text>
                                <SelectRangeFieldControlled
                                    name="rooms"
                                    options={[
                                        {id: "1", label: "1", value: 1},
                                        {id: "2", label: "2", value: 2},
                                        {id: "3", label: "3", value: 3},
                                        {id: "4", label: "4", value: 4},
                                        {id: "5", label: "5+", value: 5}
                                    ]}
                                    className={roomsSelectStyle}
                                    itemClassName={rangeSelectItem}
                                />
                            </div>
                        </div>
                        <div className={switcherDesktop}>
                            <SwitcherControlled labelContent={FOR_YOU_FORM_LABELS.creditPlan} name="creditPlan" wrapperClassName={switchStyle} />
                        </div>
                        <Button variant="filled_primary" size="medium" type="submit" className={btnStyle}>
                            Zapisz swoje preferencje
                        </Button>
                    </motion.div>
                    <AnimatePresence>
                        {isLocationFocused && (
                            <motion.div
                                initial={{opacity: 0, height: 0}}
                                animate={{opacity: 1, height: "auto"}}
                                exit={{opacity: 0, height: 0}}
                                transition={transition}
                            >
                                <div className={mapHolder}>
                                    <PreferencesRegionMap onActionButtonClick={onActionButtonClick} />
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </form>
        </div>
    );
};

PreferencesForm.Header = PreferencesFormHeader;

const transition = {
    type: "spring",
    stiffness: 300,
    damping: 30
};

const formStyle = css({
    px: 1.5,
    py: 2,
    borderRadius: 1,

    md: {
        p: 2
    }
});

const formLocationStyle = css({
    background: "var(--colors-gray-100)",

    md: {
        mt: 3
    }
});

const locationHeading = css({
    pt: 2.5,
    pl: 1.5,

    md: {
        p: 0
    }
});

const preferencesWrapper = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "column",
    gap: 3,
    mt: 2,

    md: {
        alignItems: "start",
        flexDirection: "row"
    }
});

const switcher = css.raw({
    p: 1,
    borderRadius: 1,
    background: "var(--colors-gray-200)"
});

const switcherDesktop = css({
    ...switcher,
    display: "none",
    mt: 2,

    md: {
        display: "block"
    }
});

const switcherMobile = css({
    ...switcher,
    width: "100%",

    md: {
        display: "none"
    }
});

const btnStyle = css({
    width: "100%",
    mt: 2
});

const roomsSelectWrapper = css({
    display: "flex",
    flexDirection: "column",
    gap: 0.5
});

const roomsSelectStyle = css({
    width: "300px"
});

const sliderStyle = css({
    pl: 1,

    md: {
        pl: 0
    }
});

const inputStyle = css({
    width: "75px"
});

const rangeSelectItem = css({
    height: "48px",
    borderColor: "var(--colors-gray-600) !important"
});

const switchStyle = css({
    display: "flex",
    justifyContent: "flex-end",
    flexDirection: "row-reverse",
    gap: 1
});

const mapHolder = css({
    minHeight: "460px"
});
